import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ButtonMC from '../../../components/ButtonMC';
import IconMC from '../../../components/IconMC';
import WhatsappPhoneMockupMC from './WhatsappPhoneMockupMC';
import { isSyncBlocked } from '../utils/whatsappFlowsSyncUtils';

const BENEFIT_CARDS = [
  { key: 'lead_capture', icon: 'person-add-outline', iconClassName: 'tw-text-[#1877f2]' },
  { key: 'guided_purchase', icon: 'credit-card-outline', iconClassName: 'tw-text-[#25d366]' },
  { key: 'instant_support', icon: 'flash-outline', iconClassName: 'tw-text-[#4B89BF]' }
];

const FLOW_STEPS = [
  { key: 'create', icon: 'edit-outline' },
  { key: 'sync', icon: 'sync-outline' },
  { key: 'launch', icon: 'message-circle-outline' }
];

const useInView = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || isVisible) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible]);

  return [ref, isVisible];
};

const Reveal = ({ children, className = '' }) => {
  const [ref, isVisible] = useInView();

  return (
    <div
      ref={ref}
      className={[
        'tw-transition-all tw-duration-700 tw-ease-out',
        isVisible ? 'tw-translate-y-0 tw-opacity-100' : 'tw-translate-y-4 tw-opacity-0',
        className
      ].join(' ')}
    >
      {children}
    </div>
  );
};

const META_FLOWS_DOCS_URL = 'https://developers.facebook.com/docs/whatsapp/flows';

const WhatsappFlowsLandingMC = ({ onSync, syncing = false, syncState = 'initial', onCreate }) => {
  const { t } = useTranslation('WhatsappFlowsScreenMC');
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  const showBspAlert = isSyncBlocked(retailer_info);
  const showSyncEmptyAlert = syncState === 'synced_empty';
  const isCompactView = showSyncEmptyAlert;

  const handleContactSupport = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  const useCases = [
    {
      key: 'lead_qualification',
      fields: [
        { label: t('landing.use_cases.items.lead_qualification.fields.name_label'), value: t('landing.use_cases.items.lead_qualification.fields.name_value') },
        { label: t('landing.use_cases.items.lead_qualification.fields.budget_label'), value: t('landing.use_cases.items.lead_qualification.fields.budget_value'), isSelect: true },
        { label: t('landing.use_cases.items.lead_qualification.fields.city_label'), value: t('landing.use_cases.items.lead_qualification.fields.city_value') },
        { label: t('landing.use_cases.items.lead_qualification.fields.product_label'), value: t('landing.use_cases.items.lead_qualification.fields.product_value'), isSelect: true }
      ]
    },
    {
      key: 'order_recovery',
      fields: [
        { label: t('landing.use_cases.items.order_recovery.fields.order_label'), value: t('landing.use_cases.items.order_recovery.fields.order_value') },
        { label: t('landing.use_cases.items.order_recovery.fields.address_label'), value: t('landing.use_cases.items.order_recovery.fields.address_value') },
        { label: t('landing.use_cases.items.order_recovery.fields.payment_label'), value: t('landing.use_cases.items.order_recovery.fields.payment_value'), isSelect: true }
      ]
    },
    {
      key: 'bookings',
      fields: [
        { label: t('landing.use_cases.items.bookings.fields.service_label'), value: t('landing.use_cases.items.bookings.fields.service_value'), isSelect: true },
        { label: t('landing.use_cases.items.bookings.fields.date_label'), value: t('landing.use_cases.items.bookings.fields.date_value') },
        { label: t('landing.use_cases.items.bookings.fields.time_label'), value: t('landing.use_cases.items.bookings.fields.time_value'), isSelect: true }
      ]
    }
  ];

  return (
    <div className="tw-wrapper tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-gradient-to-b tw-from-[#f2fff6] tw-via-white tw-to-white tw-p-6 sm:tw-p-8 lg:tw-p-10">
      <div className="tw-pointer-events-none tw-absolute tw--top-24 tw--right-20 tw-h-64 tw-w-64 tw-rounded-full tw-bg-[#25d366]/10" />
      <div className="tw-pointer-events-none tw-absolute tw--bottom-20 tw--left-12 tw-h-56 tw-w-56 tw-rounded-full tw-bg-[#1877f2]/10" />

      {showBspAlert && (
        <div
          className="tw-relative tw-z-10 tw-mb-6 tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
          role="alert"
        >
          <div className="tw-flex tw-items-start tw-gap-3">
            <IconMC name="alert-circle-outline" className="tw-mt-0.5 tw-shrink-0 tw-text-amber-600" size="lg" />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-amber-900">
                {t('landing.bsp_alert.title')}
              </p>
              <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-amber-800">
                {t('landing.bsp_alert.description')}
              </p>
            </div>
          </div>
          <ButtonMC
            text={t('landing.bsp_alert.cta')}
            onClick={handleContactSupport}
            icon="message-circle-outline"
            iconPosition="left"
            size="small"
            variant="SECONDARY"
            className="tw-shrink-0 !tw-rounded-lg !tw-border-amber-300 !tw-bg-white !tw-text-amber-900 hover:!tw-bg-amber-100"
            iconClassName="!tw-text-amber-700"
          />
        </div>
      )}

      {showSyncEmptyAlert && (
        <div
          className="tw-relative tw-z-10 tw-mb-6 tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-4 tw-py-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
          role="status"
        >
          <div className="tw-flex tw-items-start tw-gap-3">
            <IconMC name="info-outline" className="tw-mt-0.5 tw-shrink-0 tw-text-blue-600" size="lg" />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-blue-900">
                {t('landing.sync_empty.title')}
              </p>
              <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-blue-800">
                {t('landing.sync_empty.description')}
              </p>
            </div>
          </div>
          <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2 sm:tw-flex-row">
            <ButtonMC
              text={t('create_flow')}
              onClick={onCreate}
              icon="plus-outline"
              iconPosition="left"
              size="small"
              className="!tw-rounded-lg"
            />
            <ButtonMC
              text={t('landing.sync_empty.cta_meta')}
              onClick={() => window.open(META_FLOWS_DOCS_URL, '_blank', 'noopener,noreferrer')}
              icon="external-link-outline"
              iconPosition="right"
              size="small"
              variant="SECONDARY"
              className="!tw-rounded-lg !tw-border-blue-300 !tw-bg-white !tw-text-blue-900 hover:!tw-bg-blue-100"
              iconClassName="!tw-text-blue-700"
            />
            <ButtonMC
              text={t('landing.sync_empty.cta_resync')}
              onClick={onSync}
              loading={syncing}
              icon="sync-outline"
              iconPosition="left"
              size="small"
              className="!tw-rounded-lg !tw-border-0 !tw-bg-[#25d366] hover:!tw-bg-[#20bd5a] !tw-text-white"
              iconClassName="!tw-text-white"
            />
          </div>
        </div>
      )}

      <Reveal>
        <section className="tw-grid tw-gap-8 lg:tw-grid-cols-[1.1fr_0.9fr] lg:tw-items-center">
          <div>
            {!isCompactView && (
              <div className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-[#25d366]/15 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-[#128c7e]">
                <IconMC name="star-outline" className="tw-size-4" />
                {t('landing.badge')}
              </div>
            )}
            <h2 className={`tw-font-semibold tw-tracking-tight tw-text-gray-900 ${isCompactView ? 'tw-text-2xl' : 'tw-mt-4 tw-text-3xl sm:tw-text-4xl'}`}>
              {isCompactView ? t('landing.sync_empty.hero_title') : t('landing.hero_title')}
            </h2>
            <p className="tw-mt-4 tw-max-w-2xl tw-text-base tw-leading-7 tw-text-gray-600">
              {isCompactView ? t('landing.sync_empty.hero_description') : t('landing.hero_description')}
            </p>
            <div className="tw-mt-6 tw-flex tw-flex-wrap tw-gap-3">
              <ButtonMC
                text={t('create_flow')}
                onClick={onCreate}
                icon="plus-outline"
                iconPosition="left"
                size="medium"
                className="!tw-rounded-xl"
              />
              {!isCompactView && (
                <ButtonMC
                  text={t('landing.primary_cta')}
                  onClick={onSync}
                  loading={syncing}
                  icon="sync-outline"
                  iconPosition="right"
                  size="medium"
                  variant="SECONDARY"
                  outlined
                  className="!tw-rounded-xl"
                />
              )}
            </div>
          </div>

          {!isCompactView && (
            <div className="tw-rounded-3xl tw-border tw-border-gray-200 tw-bg-white tw-p-5 tw-shadow-sm sm:tw-p-6">
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-gray-500">
                {t('landing.preview_label')}
              </p>
              <h3 className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-gray-900">
                {t('landing.preview_title')}
              </h3>
              <div className="tw-mt-5 tw-space-y-3">
                <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-p-3">
                  <IconMC name="edit-outline" className="tw-text-[#1877f2]" />
                  <p className="tw-text-sm tw-font-medium tw-text-gray-700">{t('landing.preview_step_create')}</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-p-3">
                  <IconMC name="sync-outline" className="tw-text-[#25d366]" />
                  <p className="tw-text-sm tw-font-medium tw-text-gray-700">{t('landing.preview_step_sync')}</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-p-3">
                  <IconMC name="message-circle-outline" className="tw-text-[#4B89BF]" />
                  <p className="tw-text-sm tw-font-medium tw-text-gray-700">{t('landing.preview_step_send')}</p>
                </div>
              </div>
            </div>
          )}
        </section>
      </Reveal>

      {!isCompactView && (
      <Reveal className="tw-mt-10">
        <section>
          <h3 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-gray-900">
            {t('landing.benefits_title')}
          </h3>
          <p className="tw-mt-2 tw-text-sm tw-leading-6 tw-text-gray-600">
            {t('landing.benefits_subtitle')}
          </p>
          <div className="tw-mt-5 tw-grid tw-gap-4 md:tw-grid-cols-3">
            {BENEFIT_CARDS.map((benefit) => (
              <div key={benefit.key} className="tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-5 tw-shadow-sm">
                <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-gray-100">
                  <IconMC name={benefit.icon} className={benefit.iconClassName} />
                </div>
                <h4 className="tw-mt-4 tw-text-base tw-font-semibold tw-text-gray-900">
                  {t(`landing.benefits.${benefit.key}.title`)}
                </h4>
                <p className="tw-mt-2 tw-text-sm tw-leading-6 tw-text-gray-600">
                  {t(`landing.benefits.${benefit.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </section>
      </Reveal>
      )}

      {!isCompactView && (
      <Reveal className="tw-mt-10">
        <section>
          <h3 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-gray-900">
            {t('landing.use_cases_title')}
          </h3>
          <p className="tw-mt-2 tw-text-sm tw-leading-6 tw-text-gray-600">
            {t('landing.use_cases_subtitle')}
          </p>
          <div className="tw-mt-6 tw-grid tw-gap-5 xl:tw-grid-cols-3">
            {useCases.map((useCase) => (
              <article
                key={useCase.key}
                className="tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-5 tw-shadow-sm tw-transition-transform tw-duration-200 hover:tw--translate-y-1"
              >
                <WhatsappPhoneMockupMC
                  contactName={t(`landing.use_cases.items.${useCase.key}.contact_name`)}
                  companyMessage={t(`landing.use_cases.items.${useCase.key}.company_message`)}
                  ctaLabel={t(`landing.use_cases.items.${useCase.key}.cta_label`)}
                  formTitle={t(`landing.use_cases.items.${useCase.key}.form_title`)}
                  submitLabel={t(`landing.use_cases.items.${useCase.key}.submit_label`)}
                  fields={useCase.fields}
                  onlineLabel={t('landing.mockup.online')}
                />

                <div className="tw-mt-4">
                  <p className="tw-text-base tw-font-semibold tw-text-gray-900">
                    {t(`landing.use_cases.items.${useCase.key}.title`)}
                  </p>
                  <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">
                    {t(`landing.use_cases.items.${useCase.key}.description`)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </Reveal>
      )}

      <Reveal className="tw-mt-10">
        <section className="tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-6 tw-shadow-sm">
          <h3 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-gray-900">
            {t('landing.how_it_works_title')}
          </h3>
          <div className="tw-mt-5 tw-space-y-4">
            {FLOW_STEPS.map((step, index) => (
              <div key={step.key} className="tw-flex tw-items-start tw-gap-3">
                <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#1877f2] tw-text-sm tw-font-semibold tw-text-white">
                  {index + 1}
                </div>
                <div className="tw-flex-1 tw-rounded-xl tw-bg-gray-50 tw-p-3">
                  <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-gray-900">
                    <IconMC name={step.icon} className="tw-text-[#25d366]" />
                    {t(`landing.how_it_works.steps.${step.key}.title`)}
                  </p>
                  <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">
                    {t(`landing.how_it_works.steps.${step.key}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      {!isCompactView && (
      <Reveal className="tw-mt-10">
        <section className="tw-rounded-2xl tw-border tw-border-[#25d366]/30 tw-bg-[#25d366]/5 tw-p-6 sm:tw-p-8">
          <h3 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-gray-900">
            {t('landing.final_title')}
          </h3>
          <p className="tw-mt-2 tw-max-w-3xl tw-text-sm tw-leading-6 tw-text-gray-700">
            {t('landing.final_description')}
          </p>
          <div className="tw-mt-5 tw-flex tw-flex-wrap tw-gap-3">
            <ButtonMC
              text={t('create_flow')}
              onClick={onCreate}
              icon="plus-outline"
              iconPosition="left"
              size="medium"
              className="!tw-rounded-xl"
            />
            <ButtonMC
              text={t('landing.final_cta')}
              onClick={onSync}
              loading={syncing}
              icon="arrow-forward-outline"
              iconPosition="right"
              size="medium"
              variant="SECONDARY"
              outlined
              className="!tw-rounded-xl"
            />
          </div>
        </section>
      </Reveal>
      )}
    </div>
  );
};

export default WhatsappFlowsLandingMC;
